/* LUXE Spotlight — dynamic Loox review feed (site-redesign-2026 ch.7)
   Loaded ONLY when the section's "Pull reviews from Loox" toggle is on
   (zero cost when off). IntersectionObserver-gated (never fetches on page
   load), sessionStorage-cached for 24h, fail-silent (console.debug only).

   Renders live reviews into the EXISTING .luxe-spotlight__row / __card
   markup and classes — it invents no card styles. The editor Review blocks
   are the fallback layer: if the fetch fails, returns nothing usable, or the
   toggle is off, those blocks render as normal. On success the live feed
   REPLACES them. Reveal/stagger is left to luxe-motion.js + the foundation
   reveal CSS (reduced-motion already disables it there).

   Spec: DESIGN-SYSTEM.md §7 (motion) + §8 (spotlight). No dependencies. */
(function () {
  'use strict';

  var CACHE_MS = 24 * 60 * 60 * 1000; // 24h
  var QUOTE_MAX = 140;
  var STAR = 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z';
  var SVG_NS = 'http://www.w3.org/2000/svg';

  function debug(msg, err) { try { console.debug('[luxe-spotlight] ' + msg, err || ''); } catch (e) {} }

  // Truncate to ~140 chars at a word boundary, append literal "..." (never an ellipsis glyph)
  function truncate(str) {
    var s = (str || '').replace(/\s+/g, ' ').trim();
    if (s.length <= QUOTE_MAX) return s;
    var cut = s.slice(0, QUOTE_MAX);
    var sp = cut.lastIndexOf(' ');
    if (sp > 80) cut = cut.slice(0, sp);
    return cut.replace(/[\s.,;:!?-]+$/, '') + '...';
  }

  // Accept a photo URL only from the Loox CDN over https (defence-in-depth vs injection)
  function safePhoto(media) {
    if (!media || !media.length) return '';
    for (var i = 0; i < media.length; i++) {
      var m = media[i];
      if (m && m.type === 'photo' && typeof m.url === 'string' && /^https:\/\//.test(m.url)) return m.url;
    }
    return ''; // videos and anything non-photo are skipped
  }

  // Map a raw Loox review (store OR product endpoint shape) to a card model, or null
  function toCard(r, minStars) {
    if (!r || typeof r.rating !== 'number' || r.rating < minStars) return null;
    var text = r.body || r.text || '';
    if (!text.trim()) return null;
    return {
      quote: truncate(text),
      name: ((r.reviewer && r.reviewer.name) || '').trim(),
      stars: Math.max(1, Math.min(5, Math.round(r.rating))),
      photo: safePhoto(r.media)
    };
  }

  function starsEl(n) {
    var wrap = document.createElement('span');
    wrap.className = 'luxe-spotlight__stars';
    wrap.setAttribute('role', 'img');
    wrap.setAttribute('aria-label', n + ' out of 5 stars');
    for (var i = 1; i <= 5; i++) {
      var svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('class', 'luxe-spotlight__star' + (i > n ? ' luxe-spotlight__star--empty' : ''));
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
      var path = document.createElementNS(SVG_NS, 'path');
      path.setAttribute('d', STAR);
      svg.appendChild(path);
      wrap.appendChild(svg);
    }
    return wrap;
  }

  // Build a card with textContent / setAttribute only — never innerHTML with API strings
  function cardEl(model, i) {
    var li = document.createElement('li');
    li.className = 'luxe-spotlight__card';
    li.style.setProperty('--i', i);

    if (model.photo) {
      var img = document.createElement('img');
      img.className = 'luxe-spotlight__photo';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.width = 560; img.height = 420; // matches the 4/3 CSS aspect
      img.alt = model.name ? ('Review photo from ' + model.name) : 'Customer review photo';
      img.src = model.photo;
      li.appendChild(img);
    }

    var body = document.createElement('div');
    body.className = 'luxe-spotlight__body';

    var q = document.createElement('blockquote');
    q.className = 'luxe-spotlight__quote';
    q.textContent = model.quote;
    body.appendChild(q);

    var meta = document.createElement('div');
    meta.className = 'luxe-spotlight__meta';
    meta.appendChild(starsEl(model.stars));
    if (model.name) {
      var nm = document.createElement('span');
      nm.className = 'luxe-spotlight__name';
      nm.textContent = model.name;
      meta.appendChild(nm);
    }
    body.appendChild(meta);

    li.appendChild(body);
    return li;
  }

  function buildUrl(endpoint, pool) {
    if (/[?&]limit=/.test(endpoint)) return endpoint;
    return endpoint + (endpoint.indexOf('?') === -1 ? '?' : '&') + 'limit=' + pool;
  }

  function selectCards(reviews, minStars, limit) {
    var cards = [];
    for (var i = 0; i < reviews.length; i++) {
      var c = toCard(reviews[i], minStars);
      if (c) cards.push(c);
    }
    // Prefer photo reviews (stable: photos first, otherwise API order preserved)
    cards.sort(function (a, b) { return (b.photo ? 1 : 0) - (a.photo ? 1 : 0); });
    return cards.slice(0, limit);
  }

  // Replace the fallback blocks with the live feed. Returns true if anything rendered.
  function inject(row, cards) {
    if (!cards || !cards.length) return false;
    var frag = document.createDocumentFragment();
    for (var i = 0; i < cards.length; i++) frag.appendChild(cardEl(cards[i], i));
    row.textContent = '';         // drop the manual fallback cards
    row.appendChild(frag);        // keeps the row's existing .in reveal state, if any
    row.setAttribute('data-loox-loaded', '');
    return true;
  }

  function settle(row, ok) {
    row.removeAttribute('data-loox-pending'); // release the reserved height
    if (!ok && !row.querySelector('.luxe-spotlight__card')) {
      // No live cards AND no manual fallback cards → hide the empty row (no orphan box)
      row.classList.add('luxe-spotlight__row--empty');
    }
  }

  function run(row) {
    var endpoint = row.getAttribute('data-loox-endpoint') || '';
    var limit = parseInt(row.getAttribute('data-loox-limit'), 10) || 6;
    var minStars = parseInt(row.getAttribute('data-loox-min-stars'), 10) || 5;

    if (!/^https:\/\//.test(endpoint)) { debug('no valid endpoint'); settle(row, false); return; }

    var key = 'luxe-spotlight:' + endpoint + '|' + limit + '|' + minStars;

    // 1) Fresh cache → render with ZERO network requests
    try {
      var raw = sessionStorage.getItem(key);
      if (raw) {
        var cached = JSON.parse(raw);
        if (cached && Array.isArray(cached.cards) && (Date.now() - cached.t) < CACHE_MS) {
          settle(row, inject(row, cached.cards));
          return;
        }
      }
    } catch (e) { debug('cache read failed', e); }

    // 2) Fetch a pool (3x the display count, capped) then filter → photo-first → slice
    var pool = Math.min(30, Math.max(limit * 3, 12));
    fetch(buildUrl(endpoint, pool), { credentials: 'omit', mode: 'cors' })
      .then(function (res) { if (!res.ok) throw new Error('HTTP ' + res.status); return res.json(); })
      .then(function (data) {
        var cards = selectCards((data && data.reviews) || [], minStars, limit);
        var ok = inject(row, cards);
        if (ok) { try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), cards: cards })); } catch (e) {} }
        settle(row, ok);
      })
      .catch(function (e) { debug('fetch failed, keeping fallback', e); settle(row, false); });
  }

  function init() {
    // In the Shopify theme editor, leave the manual blocks in place so they stay editable.
    if (window.Shopify && window.Shopify.designMode) {
      document.querySelectorAll('.luxe-spotlight__row[data-loox-dynamic]').forEach(function (r) {
        r.removeAttribute('data-loox-pending');
      });
      return;
    }

    var rows = document.querySelectorAll('.luxe-spotlight__row[data-loox-dynamic]');
    if (!rows.length) return;

    if (!('IntersectionObserver' in window)) {
      // No IO support: don't fetch on load (perf-first). Keep the manual fallback, release height.
      Array.prototype.forEach.call(rows, function (r) { r.removeAttribute('data-loox-pending'); });
      return;
    }

    // Fetch only as the section approaches the viewport — never on page load.
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        io.unobserve(e.target);
        run(e.target);
      });
    }, { rootMargin: '400px 0px' });

    Array.prototype.forEach.call(rows, function (r) { io.observe(r); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
